const en = {
  brand: "Offertli",
  nav: {
    dashboard: "Dashboard",
    customers: "Customers",
    requests: "Requests",
    offers: "Offers",
    projects: "Projects",
    invoices: "Invoices",
    catalog: "Inventory",
    organization: "Organization",
    users: "Users",
    settings: "Settings",
    more: "More",
    newRequest: "New request",
    notifications: "Notifications",
    main: "Main navigation",
  },
  userMenu: {
    label: "User menu",
    language: "Language",
    logout: "Sign out",
  },
  theme: {
    label: "Appearance",
    system: "System",
    light: "Light",
    dark: "Dark",
  },
  locale: {
    en: "English",
    de: "Deutsch",
    fr: "Français",
    es: "Español",
  },
  common: {
    save: "Save",
    cancel: "Cancel",
    back: "Back",
    next: "Next",
    continue: "Continue",
    submit: "Submit",
    close: "Close",
    loading: "Loading…",
    retry: "Retry",
    yes: "Yes",
    no: "No",
    optional: "optional",
    required: "required",
    add: "Add",
    remove: "Remove",
    edit: "Edit",
    delete: "Delete",
    email: "Email",
    password: "Password",
    firstName: "First name",
    lastName: "Last name",
    phone: "Phone",
    website: "Website",
    name: "Name",
    stepOf: "Step {{current}} of {{total}}",
  },
  validation: {
    required: "This field is required.",
    email: "Enter a valid email address.",
    minLength: "Must be at least {{min}} characters.",
    maxLength: "Must be at most {{max}} characters.",
    passwordMin: "Password must be at least {{min}} characters.",
    passwordMismatch: "Passwords do not match.",
    iban: "Enter a valid IBAN.",
    qrIbanRequired: "A QR-IBAN is required for QR references (QRR).",
    termsRequired: "You must accept the terms to continue.",
    rateRange: "Rate must be between 0 and 100.",
    invalidNumber: "Enter a valid number.",
    tokenRequired: "Enter your setup token.",
    minOneRate: "At least one tax rate is required.",
    passwordLetter: "Password must contain at least one letter.",
    passwordNumber: "Password must contain at least one number.",
    passwordPolicy:
      "Use at least 10 characters, including a letter and a number.",
    localeUnsupported: "Select one of the supported languages.",
    invalidDate: "Enter a valid date.",
    invalidTime: "Enter a valid time.",
    endAfterStart: "The end time must be later than the start time.",
    completeDateTime: "Enter both a date and a 24-hour time (HH:mm).",
  },
  errors: {
    generic: "Something went wrong. Please try again.",
    invalidFields: "Review the highlighted fields and correct any errors.",
    network: "Cannot reach the server. Check your connection.",
    sessionExpired: "Your session has expired. Please sign in again.",
    forbidden: "You do not have permission to access this page.",
    invalidCredentials: "Invalid email or password.",
    tooManyAttempts: "Too many attempts. Please try again later.",
  },
  forbidden: {
    title: "No access",
    message:
      "Your account does not have the permission required to view this page.",
    backHome: "Back to dashboard",
  },
  status: {
    draft: "Draft",
    submitted: "Submitted",
    in_process: "In process",
    processed: "Processed",
    ready: "Ready",
    sent: "Sent",
    accepted: "Accepted",
    rejected: "Rejected",
    declined: "Declined",
    planned: "Planned",
    scheduled: "Scheduled",
    in_progress: "In progress",
    on_hold: "On hold",
    completed: "Completed",
    issued: "Issued",
    credited: "Credited",
    unpaid: "Unpaid",
    partially_paid: "Partially paid",
    paid: "Paid",
    overpaid: "Overpaid",
    overdue: "Overdue",
    low: "Low",
    normal: "Normal",
    high: "High",
    invited: "Invited",
    active: "Active",
    suspended: "Suspended",
    deactivated: "Deactivated",
  },
  auth: {
    currentPassword: "Current password",
    newPassword: "New password",
    login: {
      title: "Sign in",
      subtitle: "Sign in to your Offertli account.",
      email: "Email",
      password: "Password",
      rememberMe: "Stay signed in",
      submit: "Sign in",
      submitting: "Signing in…",
      forgot: "Forgot your password?",
    },
    forgot: {
      title: "Reset password",
      subtitle: "Enter your email address and we will send you a reset link.",
      submit: "Send reset link",
      sentTitle: "Check your inbox",
      sentMessage:
        "If an account exists for this email address, you will receive a password reset link shortly.",
      backToLogin: "Back to sign in",
    },
    reset: {
      title: "Choose a new password",
      subtitle: "Set a new password for your account.",
      newPassword: "New password",
      confirmPassword: "Confirm password",
      submit: "Set new password",
      successTitle: "Password updated",
      successMessage: "Your password has been changed. You can now sign in.",
      missingToken: "This reset link is invalid or has expired.",
      toLogin: "Continue to sign in",
    },
  },
  invite: {
    title: "Accept invitation",
    subtitle:
      "Welcome to Offertli. Set your password to activate your account.",
    invitedAs: "You were invited as",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm password",
    acceptTerms: "I accept the terms of use and privacy notice.",
    submit: "Activate account",
    invalidTitle: "Invitation invalid",
    invalidMessage:
      "This invitation link is invalid, has expired or has already been used.",
    toLogin: "Go to sign in",
  },
  setup: {
    title: "Set up Offertli",
    subtitle: "Configure your company in a few steps.",
    alreadyDone: "Setup is already complete.",
    steps: {
      credential: "Access",
      language: "Language",
      industry: "Industry",
      company: "Company",
      address: "Address",
      branding: "Branding",
      systemAdmin: "System administrator",
      localization: "Localization",
      vat: "VAT",
      bank: "Bank & QR-bill",
      numbering: "Numbering",
      review: "Review",
    },
    credential: {
      title: "Verify access",
      description:
        "Enter the setup token from your server configuration, or open the invitation link you received by email.",
      tokenLabel: "Setup token",
      tokenHint:
        "You will find the token in your deployment environment (OFFERTLI_SETUP_TOKEN).",
      validate: "Validate token",
      validating: "Validating…",
      invalid: "This token is invalid or setup has already been completed.",
      inviteDetected: "Invitation link detected",
      inviteValid: "Invitation for {{email}} verified.",
      inviteInvalid: "This invitation link is invalid or has expired.",
      useToken: "Use a setup token instead",
    },
    language: {
      title: "Interface language",
      description:
        "Choose the language for this setup wizard and your own interface.",
    },
    industry: {
      label: "Industry",
      description:
        "Choose the industry that best matches your company. We will prepare suitable default categories.",
      options: {
        landscaping: "Landscape gardening",
        general_services: "General services",
        construction: "Construction",
        cleaning: "Cleaning",
        other: "Other",
      },
    },
    company: {
      title: "Company identity",
      name: "Company name",
      tradingName: "Trading name",
      vatNumber: "VAT / UID number",
      website: "Website",
    },
    address: {
      title: "Address & contact",
      line1: "Address line 1",
      line2: "Address line 2",
      postalCode: "Postal code",
      city: "City",
      canton: "Canton / region",
      country: "Country",
      email: "Company email",
      phone: "Phone",
    },
    systemAdmin: {
      title: "System administrator",
      description:
        "Set the password for the built-in system administrator. No employee or user account is created.",
      username: "Username",
    },
    localization: {
      title: "Locale, timezone & currency",
      locale: "Default language",
      localeHint: "Used for new users and documents when no preference is set.",
      timezone: "Timezone",
      currency: "Currency",
    },
    vat: {
      title: "VAT & tax rates",
      registered: "VAT registered",
      registeredHint: "Enable if your company is registered for VAT.",
      rates: "Tax rates",
      ratesHint: "Define the VAT rates available on offers and invoices.",
      rateName: "Rate name",
      rateValue: "Rate (%)",
      isDefault: "Default",
      addRate: "Add rate",
      standardRate: "Standard rate",
      noVat: "No VAT",
    },
    bank: {
      title: "Bank & QR-bill",
      description: "Used to generate Swiss QR-bills on invoices.",
      iban: "IBAN",
      ibanHint: "The IBAN payments should be sent to.",
      qrIbanHint: "QRR references require a QR-IBAN (QR-IID 30000–31999).",
      bankName: "Bank name",
      referenceType: "Reference type",
      refNON: "No reference (NON)",
      refSCOR: "Creditor reference (SCOR)",
      refQRR: "QR reference (QRR)",
      paymentTermDays: "Payment term (days)",
    },
    numbering: {
      title: "Numbering & document defaults",
      description:
        "Document numbers are generated automatically. You can adjust prefixes and formats later in the settings.",
      paymentTermDays: "Payment term (days)",
      offerValidityDays: "Offer validity (days)",
      previewTitle: "Default number formats",
      customer: "Customer",
      request: "Request",
      offer: "Offer",
      project: "Project",
      invoice: "Invoice",
    },
    review: {
      title: "Review & complete",
      description: "Check your configuration before completing the setup.",
      companySection: "Company",
      systemAdminSection: "System administrator",
      localizationSection: "Localization",
      billingSection: "Billing",
      documentsSection: "Documents",
      complete: "Complete setup",
      completing: "Completing…",
      failed:
        "Setup could not be completed. Please check your entries and try again.",
    },
  },
  dashboard: {
    title: "Dashboard",
    welcome: "Welcome back, {{name}}.",
    placeholderTitle: "Your dashboard is being prepared",
    placeholderMessage:
      "Widgets for open requests, offers and invoices will appear here in the next iteration.",
  },
  notifications: {
    title: "Notifications",
    empty: "No new notifications",
  },
  notFound: {
    title: "Page not found",
    message: "The page you are looking for does not exist or has been moved.",
    backHome: "Back to dashboard",
  },
  offline: {
    title: "You are offline",
    message:
      "Offertli needs a connection to the server. Please check your network and try again.",
    retry: "Try again",
  },
} as const;

export default en;
